import { Save } from '@mui/icons-material'
import '../Profile.css'
import { Accordion, AccordionDetails, AccordionSummary, Box, InputAdornment, OutlinedInput, Tooltip } from "@mui/material"
import CircularIndeterminate from "components/blocks/progressBar/progressBar"
import ButtonLarge1 from "components/elements/ButtonLarge1"
import ButtonLarge4 from "components/elements/ButtonLarge4"
import ButtonMedium1 from 'components/elements/ButtonMedium1'
import ButtonMedium4 from "components/elements/ButtonMedium4"
import CaptionText from 'components/elements/CaptionText'
import ContentNormal from "components/elements/ContentNormal"
import CustomInput from 'components/elements/CustomInput'
import CustomSelect from "components/elements/CustomSelect"
import ExportToXlsx from "components/elements/ExportToXlsx"
import Headline2 from "components/elements/Headline2"
import Headline4 from "components/elements/Headline4"
import Headline5 from "components/elements/Headline5"
import RoundIconButton1 from "components/elements/RoundIconButton1"
import CaretDown from "components/icons/CaretDown"
import { AppContext } from "contexts/AppContext"
import moment from "moment"
import { useContext, useEffect, useState } from "react"
import { Modal } from "react-bootstrap"
import { callAPI } from "utils/API"
import { isValidEmail, numericRangeArray, pointsConvertor } from "utils/general"
import { translate } from "utils/Translate"
import IconButtonLarge from 'components/elements/IconButtonLarge'
import { useNavigate } from 'react-router-dom'

const TaxPage = () => {
    const { styleGuide, brandData } = useContext(AppContext)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [showTaxDownload, setShowTaxDownload] = useState(false)
    const [selectedYearData, setSelectedYearData] = useState([])
    const [transactions, setTransactions] = useState([])
    const [yearWiseTransactions, setYearWiseTransactions] = useState(null)
    const [selectedYear, setSelectedYear] = useState("")
    const [userPoints, setUserPoints] = useState({})
    const [showTaxPopup, setShowTaxPopup] = useState(false)

    const hideDownloadTaxInfoBasedOnYears = brandData?.features?.hideDownloadTaxInfoBasedOnYears
    const brandYear = new Date(brandData?.timeInsert).getFullYear()
    const currentYear = new Date().getFullYear()
    const activeYears = numericRangeArray(brandYear, currentYear, 1)

    const fetchActivePoints = async () => {
        const response = await callAPI("/user/getWalletActivationProfileStats", "GET");
        if (response) {
            setUserPoints(response)
            setLoading(false)
        }
    }

    const fetchWallet = async () => {
        let userWalletLines = await callAPI(`/user/walletLines`)
        const userWallet = []
        if (!userWalletLines || !userWalletLines.length) return setTransactions([])
        // remove 0 value entries, personal shop & custom order entries and SHOP & USER type entries
        userWalletLines = userWalletLines
            .filter(w => !!w.walletAmountPoints && !w.walletNote.includes("Order #") && !["SHOP", "USER"].includes(w.walletType.toUpperCase()))

        for (const walletLine of userWalletLines) {
            let receiverUserDetails = walletLine.receiverUserDetails ? walletLine.receiverUserDetails : null
            const DKK = walletLine.walletAmountPoints / (brandData?.brandPointRatio || 1)
            const walletObj = {
                year: new Date(walletLine.timeInsert).getFullYear(),
                date: moment(walletLine.timeInsert).format("DD/MM/YYYY"),
                Date: moment(walletLine.timeInsert).format("DD/MM/YYYY"),
                walletAmountPoints: walletLine.walletAmountPoints,
                Point: pointsConvertor(brandData, walletLine.walletAmountPoints, ' '),
                DKK: pointsConvertor(brandData, DKK.toFixed(2), ' '),
                walletNote: walletLine.walletNote === " " || walletLine.walletNote === "" ? "-" : walletLine.walletNote,
                Note: walletLine.walletNote === " " || walletLine.walletNote === "" ? "-" : walletLine.walletNote,
                sum: walletLine.sum,
                WalletID: walletLine.WalletID,
                Navn: receiverUserDetails?.userName ? receiverUserDetails.userName : "-",
                Email: receiverUserDetails?.userEmail ? receiverUserDetails.userEmail : "-",
                UID: receiverUserDetails?.userUID ? receiverUserDetails.userUID : "-",
            }
            userWallet.push(walletObj)
        }
        return setTransactions(userWallet)
    }

    const openTaxModal = async (e) => {
        e.stopPropagation()
        setShowTaxPopup(true)
    }

    useEffect(() => {
        fetchActivePoints()
        fetchWallet()
    }, [])
    
    useEffect(() => {
        if (!yearWiseTransactions?.length) {
            setSelectedYearData([])
        } else if (!selectedYear) {
            setSelectedYearData(transactions)
        } else {
            const data = yearWiseTransactions.filter(i => i.year === selectedYear)
            if (!data?.length) {
                setSelectedYearData([])
            } else {
                setSelectedYearData(data[0].data)
            }
        }
    }, [selectedYear, yearWiseTransactions])

    useEffect(() => {
        if (transactions) {
            let years = transactions.map(i => i.year)
            years = [...new Set(years)]
            const formattedData = years.map(y => {
                return {
                    year: y,
                    data: transactions.filter(i => y === i.year)
                }
            })
            setYearWiseTransactions(formattedData)
        }
    }, [transactions])

    const pointsThisYear = pointsConvertor(brandData, userPoints?.walletActivationCurrentYear || 0, '') 
    const dkkThisYear = userPoints?.walletActivationCurrentYearValue
    const awaitingPoints = pointsConvertor(brandData, userPoints?.walletActivationBalance || 0, '')
    const awaitingDKK = (userPoints?.walletActivationBalance / brandData.brandPointRatio) || 0
    const correctAdvanceStatementLink = brandData?.features?.correctAdvanceStatementLink || 'https://www.tastselv.skat.dk/'

    return (
        <Box flex={1} display={'flex'} flexDirection={'column'} gap={4}>
            <TaxModal show={showTaxPopup} setShow={setShowTaxPopup} />
            <Box display={'flex'} gap={4} flexWrap={'wrap'}>
                <TaxOverviewBox
                    headline={translate('_PROFILE_ACTIVATED_POINTS_THIS_YEAR_', `Du har aktiveret xx point i år,`).replace('xx', pointsThisYear)}
                    subHeadline={translate('_CORRESPONDING_TO_', 'svarende til')}
                    points={pointsConvertor(brandData, dkkThisYear, 'kr.')}
                    buttonText={translate("_CORRECT_YOUR_ADVANCE_STATEMENT_", "Ret din forskudsopgørelse")}
                    buttonFunction={() =>  {window.location.href = correctAdvanceStatementLink}}
                    textColor={styleGuide.color8}
                    loading={loading}
                />
                <TaxOverviewBox
                    headline={translate('_PROFILE_YOU_HAVE_XX_WAITING_POINTS_', `Du har xx ikke-aktiverede point,`).replace('xx', awaitingPoints)}
                    subHeadline={translate('_CORRESPONDING_TO_', 'svarende til')}
                    points={pointsConvertor(brandData, awaitingDKK.toFixed(0), 'kr.')}
                    buttonText={translate("_ACTIVATE_POINTS_", "Aktivér point")}
                    buttonFunction={() =>  navigate('/profile/points')}
                    disabled={awaitingPoints <= 0}
                    textColor={styleGuide.color12}
                    loading={loading}
                />
            </Box>

            <Accordion
                className="profileAccordion"
                defaultExpanded={true}
            >
                <AccordionSummary
                    className="profileAccordionSummary"
                    expandIcon={<RoundIconButton1 icon={<CaretDown fill="white" />} />}
                >
                    <Box display={'flex'} flexDirection={'column'} gap={2} paddingRight={1}>
                        <Headline4
                            translationKey="_TAXATION_FAQ_HEADLINE_"
                            fallbackText="Hvordan foregår beskatning?"
                        />
                        <Box display={'flex'} gap={2} flexWrap={'wrap'}>
                            {
                                !hideDownloadTaxInfoBasedOnYears &&
                                    <ButtonMedium4
                                        showIcon={false}
                                        translationKey={"_DOWNLOAD_TAX_INFO_BUTTON_TEXT_"}
                                        fallbackText={"Download skatteinformation"}
                                        onClick={(e) => { e.stopPropagation(); setShowTaxDownload(true) }}
                                    />
                            }
                            {!!brandData && !!brandData.features && !brandData.features.hideProfileTaxReportButton && <ButtonMedium4
                                showIcon={false}
                                translationKey="_PROFILE_PAGE_TAX_BUTTON_TEXT_"
                                fallbackText="Oversigt for indberetning til SKAT"
                                onClick={(e) => openTaxModal(e)}
                            />}
                            <a
                                href={correctAdvanceStatementLink}
                                target="_blank"
                                style={{ textDecoration: "none" }}
                                onClick={e => e.stopPropagation()}
                            >
                                <ButtonMedium4
                                    showIcon={false}
                                    translationKey='_CORRECT_YOUR_ADVANCE_STATEMENT_'
                                    fallbackText="Ret din forskudsopgørelse"
                                />
                            </a>
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails
                    className="profileAccordionDetails hide-scroll"
                    style={{
                        borderTop: `1px solid ${styleGuide.color4}`,
                    }}
                >
                    <Box display={'flex'} flexDirection={'column'} gap={4}>
                        <Box display={'flex'} flexDirection={'column'} gap={2}>
                            <Headline5
                                translationKey="_PROFILE_TAX_FAQ_SECTION_1_HEADLINE_"
                                fallbackText="Hvad indberettes til skattestyrelsen?"
                            />
                            <ContentNormal
                                translationKey="_PROFILE_TAX_FAQ_SECTION_1_LINE_1_"
                                fallbackText="De point, du aktiverer, indberettes automatisk til Skattestyrelsen som B-indkomst. Indberetningen sker månedligt med en måneds forsinkelse og vil være tilgængelig i den skattemappe 1-2 hverdage efter månedens start. Dette sikrer fuld gennemsigtighed og korrekt beskatning af dine point."
                            />
                            <ContentNormal>
                                {translate('_PROFILE_TAX_FAQ_SECTION_1_LINE_2_', 'Bemærk, at aktiverede point ikke kan deaktiveres, hverken af xx eller MS Branding. Sørg derfor for at bruge dine aktiverede point i shoppen.').replace('xx', brandData?.brandName)}
                            </ContentNormal>
                        </Box>

                        <Box display={'flex'} flexDirection={'column'} gap={2}>
                            <Headline5
                                translationKey="_PROFILE_TAX_FAQ_SECTION_2_HEADLINE_"
                                fallbackText="Sådan retter du din forskudsopgørelse (felt 210)"
                            />
                            <ContentNormal>
                                {translate('_PROFILE_TAX_FAQ_SECTION_2_LINE_1_', `Indtast det beløb, du forventer at tjene som honorarindtægt/B-indkomst fra xx i løbet af året, i felt 210 på forskudsopgørelsen. Feltet 210 hedder "Honorarer, indkomst fra privat dagpleje og værdien af nogle personalegoder". Feltet omfatter "Honorarer udbetalt af din hvervgiver" (B-indkomst)."`).replace('xx', brandData?.brandName).replace(/&quot;/g, '"')}
                            </ContentNormal>
                            <Box>
                                <ContentNormal
                                    translationKey="_PROFILE_TAX_FAQ_SECTION_2_LINE_2_"
                                    fallbackText="For yderligere vejledning om felt 210, klik "
                                />
                                <ContentNormal>
                                    <a href={'https://info.skat.dk/data.aspx?oid=1536748'} target='_blank'>
                                        {translate('_HERE_LINK_', 'her')}
                                    </a>
                                </ContentNormal>
                                .
                            </Box>
                        </Box>

                        <Box display={'flex'} flexDirection={'column'} gap={2}>
                            <Headline5
                                translationKey="_PROFILE_TAX_FAQ_SECTION_3_HEADLINE_"
                                fallbackText="Frekvens for indberetning"
                            />

                            <ContentNormal>
                                {translate('_PROFILE_TAX_FAQ_SECTION_3_LINE_1_', 'Som bruger afgør du selv, hvordan du vil håndtere din forskudsopgørelse. Det anbefales dog at opdatere den kvartalsvis. I xx kan du se værdien af dine aktiverede point for det aktuelle skatteår i boksen herover. Du kan justere din forskudsopgørelse flere gange om året, hvis du modtager flere B-indkomster/honorarer. Når du ændrer felt 210, kan det påvirke din skat ved at hæve din trækprocent og/eller sænke dit månedsfradrag, og evt. danne indbetalingskort. Yderligere oplysninger findes under afsnittet Betaling ved fast B-indkomst.').replace('xx', brandData?.brandName)}
                            </ContentNormal>
                            <Box>
                                <ContentNormal
                                    translationKey="_PROFILE_TAX_FAQ_SECTION_3_LINE_2_"
                                    fallbackText="Du kan rette din forskudsopgørelse "
                                />
                                <ContentNormal>
                                    <a href={correctAdvanceStatementLink} target='_blank'>
                                        {translate('_HERE_LINK_', 'her')}
                                    </a>
                                </ContentNormal>
                                .
                            </Box>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion
                className="profileAccordion"
                defaultExpanded={true}
            >
                <AccordionSummary
                    className="profileAccordionSummary"
                    expandIcon={<RoundIconButton1 icon={<CaretDown fill="white" />} />}
                >
                    <Box display={'flex'} flexDirection={'column'} gap={2}>
                        <Headline4
                            translationKey="_COMPUTE_EXPECTED_TAX_"
                            fallbackText="Beregn din forventede skat"
                        />
                        <Box display={'flex'} gap={2} flexWrap={'wrap'}>
                            <a
                                href={correctAdvanceStatementLink}
                                target="_blank"
                                style={{ textDecoration: "none" }}
                            >
                                <ButtonMedium4
                                    showIcon={false}
                                    translationKey='_CORRECT_YOUR_ADVANCE_STATEMENT_'
                                    fallbackText="Ret din forskudsopgørelse"
                                />
                            </a>
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails
                    style={{
                        borderTop: `1px solid ${styleGuide.color4}`,
                        overflow: 'scroll'
                    }}
                    className="hide-scroll profileAccordionDetails"
                >
                    <Box display={'flex'} flexDirection={'column'} gap={3}>
                        <Box display={'flex'} flexDirection={'column'} gap={2}>
                            <Headline5
                                translationKey='_PROFILE_TAX_COMPUTATION_SUB_HEADLINE_'
                                fallbackText='Se hvad du skal betale i skat'
                            />
                            <ContentNormal>
                                {translate('_PROFILE_TAX_COMPUTATION_LINE_1_', 'Indtast din trækprocent og få en beregning af din forventede skat i indeværende år.')}
                                <br />
                                {translate('_PROFILE_TAX_COMPUTATION_LINE_2_', 'Bemærk at dette kun er en vejledende beregning.')}
                            </ContentNormal>
                        </Box>
                        <EstimatedTaxForm taxDKK={dkkThisYear} />
                    </Box>

                </AccordionDetails>
            </Accordion>

            <Modal size="xl" show={showTaxDownload} onHide={() => setShowTaxDownload(false)} style={{ margin: 'auto', zIndex: 1200 }} centered >
                <Modal.Header className="border-0" closeButton style={{ fontSize: 9 }} >
                    <Headline4 
                        translationKey="_DOWNLOAD_TAX_INFO_POPUP_CAPTION_"
                        fallbackText="Year wise tax information"
                    />
                </Modal.Header>
                <Modal.Body>
                    <Box display={'flex'} flexDirection={'column'} gap={2} style={{overflowX: 'auto'}} paddingRight={2}>
                        <Box display={'flex'} justifyContent={'space-between'} alignItems={'end'} gap={1}>
                            <Box>
                                <ExportToXlsx
                                    excelData={selectedYearData}
                                    fileName={`${selectedYear || "Point"}_data_export_`}
                                    buttonText={translate("_DOWNLOAD_TAX_INFO_EXPORT_BUTTON_TEXT_", "Export")}
                                    fields={["Note", "Point", "DKK", "Date"]}
                                />
                            </Box>
                            <CustomSelect
                                label={translate("_YEAR_", "År")}
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                options={[{ value: "", label: "All" }, ...activeYears.map(i => ({ value: i, label: i }))]}
                                style={{ width: 120 }}
                            />
                        </Box>
                        {!selectedYearData ? null : !!selectedYearData && !selectedYearData.length ? (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "1rem",
                                    padding: 40,
                                }}
                            >
                                <Headline5
                                    style={{textAlign: 'center'}}
                                    translationKey="_DOWNLOAD_TAX_INFO_NO_WALLET_CAPTION_"
                                    fallbackText="Ingen transaktion i din pung endnu"
                                />
                                <ContentNormal
                                    style={{textAlign: 'center'}}
                                    translationKey="_DOWNLOAD_TAX_INFO_NO_WALLET_TEXT_"
                                    fallbackText="Når der sker en transaktion, vil du se det her. "
                                />
                            </div>
                        ) : (
                            <div className="transaction-table">
                                <div className="table-head">
                                    <div className="th tcol-xsmall"></div>
                                    <div className="th tcol-5 ">{'Note'}</div>
                                    <div className="th tcol-2">{'Point'}</div>
                                    <div className="th tcol-2">{'DKK'}</div>
                                    <div className="th tcol-3">{translate('_DATE_', 'Dato')}</div>
                                </div>
                                <div className="table-body">
                                    {selectedYearData.map((transaction, idx) => (
                                        <div className="tr" key={idx}>
                                            <div className="td tcol-xsmall"></div>
                                            <div className="td tcol-5 ">{transaction.walletNote}</div>
                                            <div className="td tcol-2">{transaction.Point}</div>
                                            <div className="td tcol-2">{transaction.DKK || ' '}</div>
                                            <div className="td tcol-3">{transaction.date}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Box>
                </Modal.Body>
            </Modal>
        </Box>
    )
}

export default TaxPage

const TaxOverviewBox = (props) => {
    const { brandData } = useContext(AppContext)
    const boxBgColor = brandData?.features?.profileBoxBgColor || '#efefef'

    return (
        <Box flex={2} style={{ backgroundColor: boxBgColor, borderRadius: 14, minWidth: 300 }} padding={4}>
            {props.loading
                ?
                    <Box display={'flex'} alignItems={'center'} justifyContent={'center'} height={200}>
                        <CircularIndeterminate />
                    </Box>
                :
                    <Box display={'flex'} flexDirection={'column'} gap={4}>
                        <Box display={'flex'} flexDirection={'column'}>
                            <Headline5 style={{ textAlign: 'center' }}>
                                {props.headline}
                            </Headline5>
                            <ContentNormal style={{ textAlign: 'center' }}>
                                {props.subHeadline}
                            </ContentNormal>
                        </Box>

                        <Headline2 style={{ textAlign: 'center', color: props.textColor }}>
                            {props.loading
                                ? 
                                    <Box display={'flex'} justifyContent={'center'}>
                                        <CircularIndeterminate />
                                    </Box>
                                :
                                    props.points
                            }
                        </Headline2>

                        <Box display={'flex'} justifyContent={'center'}>
                            <ButtonLarge4
                                showIcon={false}
                                onClick={props.buttonFunction}
                                disabled={props.disabled}
                            >
                                {props.buttonText}
                            </ButtonLarge4>
                        </Box>
                    </Box>
            }
        </Box>
    )
}

const EstimatedTaxForm = (props) => {
    const { styleGuide, userData, setUserData } = useContext(AppContext)
    const [taxPercentage, setTaxPercentage] = useState(Number(userData?.taxPercentage) || 0)
    const [taxDKK, setTaxDKK] = useState(null)
    const [loading, setLoading] = useState(false)

    const inputStyle = {
        borderRadius: 14,
        border: `1px solid ${styleGuide.color4}`,
        height: 45,
    }

    const submitTaxPercentage = async () => {
        setLoading(true)
        const response = await callAPI("/user/saveTaxPercentage", "POST", { body: { taxPercentage: taxPercentage } })
        if (response) {
            setUserData({ ...userData, taxPercentage: taxPercentage })
        }
        setLoading(false)
    }

    return (
        <Box display={'flex'} gap={2} flexWrap={'wrap'}>
            <OutlinedInput
                style={{...inputStyle, width: 80}}
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                value={taxPercentage}
                onChange={(v) => setTaxPercentage(v.target.value)}
            />

            <IconButtonLarge
                style={{
                    borderRadius: 14,
                    border: `2px solid ${styleGuide.color4}`,
                }}
                onClick={submitTaxPercentage}
                icon={loading
                    ?
                        <CircularIndeterminate
                            size={16}
                        />
                    :
                        <Tooltip title={translate('_BUTTON_CAPTION_SAVE_', 'Gem')}>
                            <Save
                                fill='white'
                                stroke={styleGuide.color16}
                            />
                        </Tooltip>
                }
            />

            <ButtonLarge1
                translationKey='_ESTIMATE_TAX_BUTTON_TEXT_'
                fallbackText='Beregn forventet skat'
                onClick={() => setTaxDKK((Math.round((taxPercentage/100) * props.taxDKK * 100) / 100).toFixed(2))}
            />

            <OutlinedInput
                style={{...inputStyle, width: 140}}
                endAdornment={<InputAdornment position="end">kr.</InputAdornment>}
                readOnly
                value={taxDKK}
            />
        </Box>
    )
}

const TaxModal = (props) => {
    const thisYear = new Date().getUTCFullYear();
    const { styleGuide, userData } = useContext(AppContext);
    const [userEmail, setUserEmail] = useState(userData.userEmail || "");
    const [taxYear, setTaxYear] = useState(thisYear - 1);
    const [sendTaxSuccess, setSendTaxSuccess] = useState("");
    const [sendTaxFailure, setSendTaxFailure] = useState("");

    const checkEmailText = translate(
        "_PROFILE_PAGE_TAX_VALIDATE_EMAIL_",
        "Please provide a valid email"
    );
    const emailSuccessText = translate(
        "_PROFILE_PAGE_TAX_SUCCESS_",
        "Skattelisten er sendt pr mail til den indtastede adresse."
    );
    const emailFailureText = translate(
        "_PROFILE_PAGE_TAX_FAILURE_",
        "Something went wrong, please try again later"
    );

    const yearOptions = [
        thisYear - 1,
        thisYear - 2,
        thisYear - 3,
        thisYear - 4,
        thisYear - 5,
    ].map((x) => {
        return { value: x, label: x };
    });

    const sendTaxDetails = async () => {
        setSendTaxFailure("");
        setSendTaxSuccess("");

        if (!userEmail || !isValidEmail(userEmail))
            return setSendTaxFailure(checkEmailText);

        const response = await callAPI("/profile/sendTaxDetails", "POST", {
            body: { year: taxYear, userEmail },
        });

        if (
            !!response &&
            !!response.message &&
            response.message.toUpperCase() === "SUCCESS"
        ) {
            return setSendTaxSuccess(emailSuccessText);
        } else {
            return setSendTaxFailure(emailFailureText);
        }
    };

    return (
        <Modal
            show={props.show}
            onHide={() => props.setShow(false)}
            style={{ margin: "auto", zIndex: 2000 }}
            centered
        >
            <Modal.Header
                className="pb-0 border-0"
                closeButton
                style={{ fontSize: 9 }}
            >
                <Headline5
                    translationKey="_PROFILE_PAGE_TAX_POPUP_CAPTION_"
                    fallbackText="Email til skattedetaljer"
                />
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                <Box display={'flex'} flexDirection={'column'} gap={4}>
                    <ContentNormal
                        translationKey="_PROFILE_PAGE_TAX_POPUP_TEXT_"
                        fallbackText="Få tilsendt en samlet oversigt over dine præmiebestillinger med en vejledende skatteværdi."
                    />
                    <CustomInput
                        wrapperStyle={{ marginBottom: 10 }}
                        label={translate("_PROFILE_PAGE_TAX_POPUP_EMAIL_FIELD_", "Email")}
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                    />
                    <CustomSelect
                        wrapperStyle={{ marginBottom: 10 }}
                        label={translate("_PROFILE_PAGE_TAX_POPUP_YEAR_FIELD_", "År")}
                        value={taxYear}
                        onChange={(e) => setTaxYear(e.target.value)}
                        options={yearOptions}
                    />
                </Box>
                <Box
                    display={'flex'}
                    justifyContent={'center'}
                    paddingY={1}
                >
                    {!!sendTaxSuccess && (
                        <CaptionText
                            style={{ color: styleGuide.color8 }}
                            fallbackText={sendTaxSuccess}
                        />
                    )}
                    {!!sendTaxFailure && (
                        <CaptionText
                            style={{ color: styleGuide.color13 }}
                            fallbackText={sendTaxFailure}
                        />
                    )}
                </Box>
            </Modal.Body>
            <Modal.Footer
                style={{
                    display: "flex",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                }}
            >
                <ButtonMedium1
                    showIcon={false}
                    translationKey="_BUTTON_SEND_MAIL_"
                    fallbackText="Send mail"
                    onClick={() => sendTaxDetails()}
                />
                <ButtonMedium4
                    showIcon={false}
                    translationKey="_BUTTON_CAPTION_CLOSE_"
                    fallbackText="Luk"
                    onClick={() => props.setShow(false)}
                />
            </Modal.Footer>
        </Modal>
    )
}
